import { ErrorColumns } from '@Arti-zen/package-backoffice'
import { CloudUpload, Download, People } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { useAppSelector } from 'hooks/useStore'
import {
  CardDescription,
  CardLabel,
  CardsRow,
  ErrorMessage,
  ExportButton,
  FileInputLabel,
  HiddenInput,
  SectionGroup,
  SectionTitle,
  UploadCard,
} from 'modules/artisan/components/ArtisanUpload'
import { pages } from 'navigation/pages'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Theme } from 'theme/theme'
import { ApiArtizenBO } from 'utils/artizenConnector'
import ErrorColumnsPopup from './ErrorColumnsPopup'

export default function UploadClientsCards() {
  const { artisan } = useAppSelector((state) => state.artisan)
  const navigate = useNavigate()

  const [errorMessageImport, setErrorMessageImport] = useState<ErrorColumns>(null)
  const [errorMessageUpdate, setErrorMessageUpdate] = useState<ErrorColumns | string | null>(null)
  const [loadingExtract, setLoadingExtract] = useState<boolean>(false)

  const handleClientToCreateList = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setErrorMessageImport(null)
    setErrorMessageUpdate(null)

    const result = await ApiArtizenBO.clients.previewClientsFromFile(file, false, String(artisan.id))

    if (result.success === false) {
      setErrorMessageImport(result.error)
      if (result?.error && 'error' in result.error) {
        console.error(result.error)
      }
    } else {
      navigate(pages.connected.artisan.uploadClients.navigate(+artisan.id), {
        state: {
          result,
          onModify: false,
        },
      })
    }
  }

  const handleClientToModifyList = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setErrorMessageImport(null)
    setErrorMessageUpdate(null)

    const result = await ApiArtizenBO.clients.previewClientsFromFile(file, true, String(artisan.id))

    if (result.success === false) {
      setErrorMessageUpdate(result.error)
      if (result?.error && 'error' in result.error) {
        console.error(result.error)
      }
    } else {
      navigate(pages.connected.artisan.uploadClients.navigate(artisan.id), {
        state: {
          result,
          onModify: true,
        },
      })
    }
  }

  const handleExtract = async () => {
    setLoadingExtract(true)
    const result = await ApiArtizenBO.clients.extract(String(artisan.id))
    setLoadingExtract(false)
    if (!result.success) {
      setErrorMessageUpdate(result.errorMessage)
      return
    }
    const blob = new Blob(['\uFEFF' + result.csv], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'clients.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const getErrorMessage = (error: ErrorColumns) => {
    switch (error?.type) {
      case 'EMPTY_ARRAY':
        return 'Le fichier envoyé contient un tableau vide.'
      case 'INCORRECT_COLUMNS':
        return `Les colonnes du fichiers sont incorrectes.${error.missingColumns.length ? `Colonnes manquantes : ${error.missingColumns.join(', ')}.` : ''}${error.extraColumns.length ? `Colonnes en trop : ${error.extraColumns.join(', ')}.` : ''}`
      case 'PROCESSING_ERROR':
        return `Le fichier n'a pas pu être chargé. `
    }
  }

  return (
    <>
      <SectionGroup>
        <SectionTitle>
          <People style={{ color: Theme.colors.blueArtizen }} />
          Clients
        </SectionTitle>
        <CardsRow>
          <UploadCard>
            <CardLabel>Ajouter des clients via un fichier CSV</CardLabel>
            <CardDescription>Vérifiez les clients avant de les importer</CardDescription>
            {errorMessageImport && errorMessageImport.type !== 'INCORRECT_COLUMNS' && (
              <ErrorMessage>{getErrorMessage(errorMessageImport)}</ErrorMessage>
            )}
            <FileInputLabel>
              <CloudUpload fontSize="small" />
              Choisir un fichier CSV
              <HiddenInput type="file" accept=".csv,.xlsx" onChange={handleClientToCreateList} />
            </FileInputLabel>
          </UploadCard>

          <UploadCard>
            <CardLabel>Modifier des clients via un fichier CSV</CardLabel>
            <CardDescription>Vérifiez les clients avant de les importer</CardDescription>

            {errorMessageUpdate && typeof errorMessageUpdate === 'string' && (
              <ErrorMessage>{errorMessageUpdate}</ErrorMessage>
            )}
            {errorMessageUpdate &&
              typeof errorMessageUpdate !== 'string' &&
              errorMessageUpdate?.type !== 'INCORRECT_COLUMNS' && (
                <ErrorMessage>{getErrorMessage(errorMessageUpdate)}</ErrorMessage>
              )}
            <ExportButton onClick={handleExtract}>
              <Download fontSize="small" />
              Importer clients existants en CSV
              {loadingExtract && <CircularProgress size={15} color="inherit" />}
            </ExportButton>
            <FileInputLabel>
              <CloudUpload fontSize="small" />
              Choisir un fichier CSV
              <HiddenInput type="file" accept=".csv,xlsx" onChange={handleClientToModifyList} />
            </FileInputLabel>
          </UploadCard>
        </CardsRow>
      </SectionGroup>

      {errorMessageImport?.type === 'INCORRECT_COLUMNS' && (
        <ErrorColumnsPopup
          extraColumns={errorMessageImport.extraColumns}
          onModify={false}
          clientsRaw={errorMessageImport.raw}
          onClose={() => setErrorMessageImport(null)}
        />
      )}

      {errorMessageUpdate &&
        typeof errorMessageUpdate !== 'string' &&
        errorMessageUpdate.type === 'INCORRECT_COLUMNS' && (
          <ErrorColumnsPopup
            extraColumns={errorMessageUpdate.extraColumns}
            onModify={true}
            clientsRaw={errorMessageUpdate.raw}
            onClose={() => setErrorMessageUpdate(null)}
          />
        )}
    </>
  )
}
